import { useQuery } from "@tanstack/react-query"
import {
  ArrowDownLeft,
  ArrowUpRight,
  Ban,
  CalendarClock,
  CalendarX,
  CircleDollarSign,
  HandCoins,
  Handshake,
  HelpCircle,
  Landmark,
  Scale,
  SplitSquareHorizontal,
  UserCheck,
  Users,
  Wallet,
} from "lucide-react"

import { api } from "@/api/cliente"
import type { CuentaCliente, CuentaEmpleado } from "@/componentes/ingresos/tipos"
import {
  kpiAnticiposClientes,
  kpiDeudaEmpleados,
  kpiPagosPorEjecutar,
  kpiSaldosProveedores,
  type OrdenPorPagarMin,
  type SaldoProveedorMin,
} from "@/componentes/kpi/calculos"
import { FilaKpi } from "@/componentes/kpi/FilaKpi"
import { esCuentaBloqueada } from "@/componentes/tesoreria/CuentaBloqueada"
import { type MontoDivisa, TarjetaKpi } from "@/componentes/ui/TarjetaKpi"

/**
 * Los indicadores de Tesorería (03/11, pedido del usuario): Movimientos, Ingresos, Pagos por
 * ejecutar y Saldos de proveedores.
 *
 * ⚠ **Movimientos e Ingresos se cuentan en el SERVIDOR** (`services/indicadores_tesoreria.py`):
 * sus listas llevan tope y contar lo cargado cambiaría con «ver más». Los demás llegan enteros
 * y se cuentan aquí, con la MISMA clave de consulta que su lista.
 */

type Cifra = { divisa: string; monto: string }
type Bolsa = { cantidad: number; montos: Cifra[] }

const montos = (cs: Cifra[]): MontoDivisa[] => cs.map((c) => ({ divisa: c.divisa, monto: c.monto }))

type IndicadoresLedger = { entradas: Bolsa; salidas: Bolsa; neto: Cifra[]; anulados: number }

/** Lo filtrado en Movimientos: recibe los MISMOS parámetros que la lista, sin su tope. */
export function KpiMovimientos({ filtros }: { filtros: URLSearchParams }) {
  const q = useQuery({
    queryKey: ["movimientos", "indicadores", filtros.toString()],
    queryFn: () => api.get<IndicadoresLedger>(`/api/v1/transacciones/indicadores?${filtros}`),
    retry: false,
  })
  // Una cuenta con PIN cerrada ya lo dice la pantalla: aquí no se repite como fallo.
  if (esCuentaBloqueada(q.error)) return null
  const k = q.data
  return (
    <FilaKpi cargando={q.isPending} error={q.isError} className="mb-4">
      {k && (
        <>
          <TarjetaKpi
            titulo="Entradas"
            detalle="Lo filtrado, sin anuladas"
            Icono={ArrowDownLeft}
            color="var(--success)"
            cantidad={k.entradas.cantidad}
            montos={montos(k.entradas.montos)}
          />
          <TarjetaKpi
            titulo="Salidas"
            detalle="Lo filtrado, sin anuladas"
            Icono={ArrowUpRight}
            color="var(--destructive)"
            cantidad={k.salidas.cantidad}
            montos={montos(k.salidas.montos)}
          />
          <TarjetaKpi
            titulo="Neto"
            detalle="Entradas menos salidas, por moneda"
            Icono={Scale}
            color="var(--primary)"
            cantidad={k.entradas.cantidad + k.salidas.cantidad}
            montos={montos(k.neto)}
            pie="movimientos vigentes"
          />
          <TarjetaKpi
            titulo="Anulados"
            detalle="Se ven tachados y no mueven el saldo"
            Icono={Ban}
            color="var(--fg-muted)"
            cantidad={k.anulados}
          />
        </>
      )}
    </FilaKpi>
  )
}

type IndicadoresIngresos = {
  por_determinar: Bolsa
  con_pagador: Bolsa
  del_mes: Bolsa
  anulados: number
}

/** Arriba de las tres secciones de Ingresos: lo que entra y lo que queda en cada cuenta. */
export function KpiIngresos() {
  const ingresos = useQuery({
    queryKey: ["ingresos", "indicadores"],
    queryFn: () => api.get<IndicadoresIngresos>("/api/v1/ingresos/indicadores"),
  })
  const clientes = useQuery({
    queryKey: ["cuentas-clientes"],
    queryFn: () => api.get<CuentaCliente[]>("/api/v1/cuentas-clientes"),
  })
  const empleados = useQuery({
    queryKey: ["cuentas-empleados"],
    queryFn: () => api.get<CuentaEmpleado[]>("/api/v1/cuentas-empleados"),
  })
  const k = ingresos.data
  const anticipos = clientes.data ? kpiAnticiposClientes(clientes.data) : null
  const deuda = empleados.data ? kpiDeudaEmpleados(empleados.data) : null
  return (
    <FilaKpi
      cargando={ingresos.isPending || clientes.isPending || empleados.isPending}
      error={ingresos.isError || clientes.isError || empleados.isError}
    >
      {k && anticipos && deuda && (
        <>
          <TarjetaKpi
            titulo="Por determinar"
            detalle="Entraron y falta decir quién pagó"
            Icono={HelpCircle}
            color="var(--warning)"
            cantidad={k.por_determinar.cantidad}
            montos={montos(k.por_determinar.montos)}
          />
          <TarjetaKpi
            titulo="Ingresado este mes"
            detalle="Por fecha valor, sin anulados"
            Icono={CircleDollarSign}
            color="var(--success)"
            cantidad={k.del_mes.cantidad}
            montos={montos(k.del_mes.montos)}
            pie={`${k.con_pagador.cantidad} con pagador en total · ${k.anulados} anulados`}
          />
          <TarjetaKpi
            titulo="Anticipos de clientes"
            detalle="Ingresado y aún sin aplicar a una CxC"
            Icono={Handshake}
            color="var(--primary)"
            cantidad={anticipos.clientes}
            montos={anticipos.montos}
            pie={anticipos.clientes === 1 ? "cliente" : "clientes"}
          />
          <TarjetaKpi
            titulo="Empleados deben"
            detalle="Anticipos y préstamos sin devolver"
            Icono={UserCheck}
            color="var(--warning)"
            cantidad={deuda.empleados}
            montos={deuda.montos}
            pie={deuda.empleados === 1 ? "empleado" : "empleados"}
          />
        </>
      )}
    </FilaKpi>
  )
}

/** Pagos por ejecutar: se le pasan las órdenes que ya trajo la lista. */
export function KpiPagosPorEjecutar({
  ordenes,
  hoy,
}: {
  ordenes: readonly OrdenPorPagarMin[]
  hoy: string
}) {
  const k = kpiPagosPorEjecutar(ordenes, hoy)
  return (
    <FilaKpi cargando={false} error={false}>
      <TarjetaKpi
        titulo="Por ejecutar"
        detalle="Aprobadas, esperando que salga el dinero"
        Icono={Wallet}
        color="var(--primary)"
        cantidad={k.todas.cuentas}
        montos={k.todas.montos}
        pie={`De ${k.proveedores} ${k.proveedores === 1 ? "proveedor" : "proveedores"}`}
      />
      <TarjetaKpi
        titulo="Vencidas"
        detalle="La fecha requerida ya pasó"
        Icono={CalendarX}
        color="var(--destructive)"
        cantidad={k.vencidas.cuentas}
        montos={k.vencidas.montos}
      />
      <TarjetaKpi
        titulo="Vencen en 7 días"
        detalle="De hoy a una semana"
        Icono={CalendarClock}
        color="var(--warning)"
        cantidad={k.semana.cuentas}
        montos={k.semana.montos}
      />
      <TarjetaKpi
        titulo="Con pago parcial"
        detalle="Ya se pagó una parte: lo que queda"
        Icono={SplitSquareHorizontal}
        color="var(--fg-muted)"
        cantidad={k.parciales.cuentas}
        montos={k.parciales.montos}
      />
    </FilaKpi>
  )
}

/** Saldos de proveedores: se le pasan los saldos que ya trajo la lista. */
export function KpiSaldosProveedores({ saldos }: { saldos: readonly SaldoProveedorMin[] }) {
  const k = kpiSaldosProveedores(saldos)
  return (
    <FilaKpi cargando={false} error={false}>
      <TarjetaKpi
        titulo="Se les debe"
        detalle="A favor de los proveedores"
        Icono={HandCoins}
        color="var(--destructive)"
        cantidad={k.aFavor.proveedores}
        montos={k.aFavor.montos}
        pie={k.aFavor.proveedores === 1 ? "proveedor" : "proveedores"}
      />
      <TarjetaKpi
        titulo="Disponible de la empresa"
        detalle="Pagado de más: se descuenta en el próximo pago"
        Icono={Landmark}
        color="var(--success)"
        cantidad={k.disponible.proveedores}
        montos={k.disponible.montos}
        pie={k.disponible.proveedores === 1 ? "proveedor" : "proveedores"}
      />
      <TarjetaKpi
        titulo="Neto"
        detalle="Lo que se debe menos lo disponible"
        Icono={Scale}
        color="var(--primary)"
        cantidad={k.conSaldo}
        montos={k.neto}
        pie="proveedores con saldo"
      />
      <TarjetaKpi
        titulo="Con las dos cosas"
        detalle="Se les debe y tienen disponible a la vez"
        Icono={Users}
        color="var(--warning)"
        cantidad={k.ambos}
      />
    </FilaKpi>
  )
}
